<script lang="ts">
  import { CATEGORIES } from "../categories";
  import { renderMarkdown } from "../markdown";
  import type { EditorState } from "../serializeFrontmatter";

  export let state: EditorState;
  export let isNew: boolean = false;
  export let saving: boolean = false;
  export let error: string | null = null;
  export let onsubmit: () => void = () => {};
  export let oncancel: () => void = () => {};
  export let ondelete: (() => void) | null = null;

  const STATUSES = ["stub", "draft", "live", "archived"];

  // Comma-separated mirrors of array fields - kept in sync with the form
  // state via the helpers below.
  let tagsText = state.tags.join(", ");
  let relatedText = state.related.join(", ");

  $: state.tags = tagsText
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
  $: state.related = relatedText
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

  let preview = false;
  $: previewHtml = preview ? renderMarkdown(state.body || "*Empty body*") : "";

  function addDoc() {
    state.docs = [...state.docs, { title: "", url: "" }];
  }
  function removeDoc(i: number) {
    state.docs = state.docs.filter((_, idx) => idx !== i);
  }
  function slugify(s: string): string {
    return s
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }
  function syncSlugFromTitle() {
    if (isNew && (!state.slug || state.slug === slugify(state.title.slice(0, -1)))) {
      state.slug = slugify(state.title);
    }
  }
</script>

<form
  class="flex flex-col gap-5"
  on:submit|preventDefault={onsubmit}
>
  {#if error}
    <div class="callout callout-warning">
      <div class="callout-icon">⚠</div>
      <div class="callout-body">{error}</div>
    </div>
  {/if}

  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <label class="flex flex-col gap-1.5 sm:col-span-2">
      <span class="text-xs uppercase tracking-wider text-text-dim">Title</span>
      <input
        bind:value={state.title}
        on:input={syncSlugFromTitle}
        type="text"
        required
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
      />
    </label>

    <label class="flex flex-col gap-1.5">
      <span class="text-xs uppercase tracking-wider text-text-dim">Slug</span>
      <input
        bind:value={state.slug}
        type="text"
        required
        readonly={!isNew}
        pattern="[a-z0-9][a-z0-9-]*"
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text font-mono outline-none focus:border-accent disabled:opacity-60"
      />
      {#if !isNew}
        <span class="text-[10px] text-text-dim">Slug is locked after creation.</span>
      {/if}
    </label>

    <label class="flex flex-col gap-1.5">
      <span class="text-xs uppercase tracking-wider text-text-dim">Category</span>
      <select
        bind:value={state.category}
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
      >
        {#each CATEGORIES as c}
          <option value={c.id}>{c.label}</option>
        {/each}
      </select>
    </label>

    <label class="flex flex-col gap-1.5">
      <span class="text-xs uppercase tracking-wider text-text-dim">Type</span>
      <select
        bind:value={state.type}
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
      >
        <option value="sop">SOP</option>
        <option value="cheat-sheet">Cheat sheet</option>
      </select>
    </label>

    <label class="flex flex-col gap-1.5">
      <span class="text-xs uppercase tracking-wider text-text-dim">Status</span>
      <select
        bind:value={state.status}
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
      >
        {#each STATUSES as s}
          <option value={s}>{s}</option>
        {/each}
      </select>
    </label>

    <label class="flex flex-col gap-1.5 sm:col-span-2">
      <span class="text-xs uppercase tracking-wider text-text-dim">One-line description</span>
      <input
        bind:value={state.description}
        type="text"
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
      />
    </label>

    <label class="flex flex-col gap-1.5">
      <span class="text-xs uppercase tracking-wider text-text-dim">Owner</span>
      <input
        bind:value={state.owner}
        type="text"
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
      />
    </label>

    <label class="flex flex-col gap-1.5">
      <span class="text-xs uppercase tracking-wider text-text-dim">Estimated minutes</span>
      <input
        bind:value={state.estimatedMinutes}
        type="number"
        min="1"
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
      />
    </label>

    <label class="flex flex-col gap-1.5">
      <span class="text-xs uppercase tracking-wider text-text-dim">Created</span>
      <input
        bind:value={state.created}
        type="date"
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
      />
    </label>

    <label class="flex flex-col gap-1.5">
      <span class="text-xs uppercase tracking-wider text-text-dim">Updated</span>
      <input
        bind:value={state.updated}
        type="date"
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
      />
    </label>

    <label class="flex flex-col gap-1.5 sm:col-span-2">
      <span class="text-xs uppercase tracking-wider text-text-dim">Tags (comma-separated)</span>
      <input
        bind:value={tagsText}
        type="text"
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
      />
    </label>

    <label class="flex flex-col gap-1.5 sm:col-span-2">
      <span class="text-xs uppercase tracking-wider text-text-dim">Related slugs (comma-separated)</span>
      <input
        bind:value={relatedText}
        type="text"
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
      />
    </label>

    <label class="flex flex-col gap-1.5 sm:col-span-2">
      <span class="text-xs uppercase tracking-wider text-text-dim">Video URL (Drive, Loom, YouTube)</span>
      <input
        bind:value={state.video}
        type="url"
        placeholder="https://drive.google.com/file/d/..."
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
      />
    </label>
  </div>

  <div class="flex flex-col gap-2">
    <div class="flex items-center justify-between">
      <span class="text-xs uppercase tracking-wider text-text-dim">Linked documents</span>
      <button type="button" class="btn-ghost text-xs" on:click={addDoc}>+ Add doc</button>
    </div>
    {#each state.docs as doc, i}
      <div class="flex gap-2">
        <input
          bind:value={doc.title}
          placeholder="Title"
          class="flex-1 bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
        />
        <input
          bind:value={doc.url}
          placeholder="https://..."
          class="flex-[2] bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text outline-none focus:border-accent"
        />
        <button type="button" class="btn-ghost px-3" on:click={() => removeDoc(i)} aria-label="Remove">×</button>
      </div>
    {/each}
  </div>

  <div class="flex flex-col gap-2">
    <div class="flex items-center justify-between">
      <span class="text-xs uppercase tracking-wider text-text-dim">Body (markdown)</span>
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="text-xs px-2 py-1 rounded-pill border border-border"
          class:bg-bg-raised={!preview}
          class:text-text={!preview}
          class:text-text-muted={preview}
          on:click={() => (preview = false)}
        >Edit</button>
        <button
          type="button"
          class="text-xs px-2 py-1 rounded-pill border border-border"
          class:bg-bg-raised={preview}
          class:text-text={preview}
          class:text-text-muted={!preview}
          on:click={() => (preview = true)}
        >Preview</button>
      </div>
    </div>
    {#if preview}
      <div class="md-body panel p-4 min-h-[300px]">
        {@html previewHtml}
      </div>
    {:else}
      <textarea
        bind:value={state.body}
        rows="20"
        spellcheck="true"
        class="bg-bg-raised border border-border rounded-md px-3 py-2 text-sm text-text font-mono outline-none focus:border-accent resize-y"
      ></textarea>
    {/if}
  </div>

  <div class="flex items-center justify-between gap-3 pt-2 border-t border-border">
    <div class="flex gap-2">
      <button type="submit" class="btn-primary" disabled={saving}>
        {saving ? "Saving..." : isNew ? "Create" : "Save changes"}
      </button>
      <button type="button" class="btn" on:click={oncancel} disabled={saving}>Cancel</button>
    </div>
    {#if ondelete}
      <button
        type="button"
        class="btn text-danger border-danger/40 hover:bg-danger/10"
        on:click={ondelete}
        disabled={saving}
      >
        Delete override
      </button>
    {/if}
  </div>
</form>
